// WHAT: Settings view component for GitHub Tauri signing keys configuration
// WHY:  Provides interface for managing private key and password for automated Tauri builds
// DOMAIN: Settings → GitHub → Signing Keys
// DEPENDENCIES: Tauri core API, secure storage for signing credentials
// RISKS:
//   - WARN: Private key and password are displayed in plain text when show toggle is enabled
//   - NOTE: Keys are cleared from state after save, but visible during editing
//   - WARN: Key deletion clears secure storage but may not invalidate active build processes

import { core } from "@tauri-apps/api";
import {
  Save,
  CheckCircle,
  XCircle,
  AlertCircle,
  Shield,
  Lock,
  Code,
  Key,
  Trash2,
  Loader2,
  Info,
  Zap,
  Eye,
  EyeOff,
} from "lucide-react";
import { useState, useEffect } from "react";

import {
  Card,
  Button,
  Stack,
  Heading,
  StatusIndicator,
  Input,
  Textarea,
  Label,
} from "@/components/ui";

/**
 * WHAT:
 *   React component for managing GitHub Tauri signing keys for automated builds.
 *
 * WHY:
 *   Enables users to configure signing credentials for local Tauri builds, providing
 *   faster build times compared to GitHub Actions while maintaining security.
 *
 * BEHAVIOR:
 *   - Checks signing key configuration status on mount
 *   - Provides form for entering private key and password
 *   - Supports show/hide toggle for sensitive fields
 *   - Saves credentials to secure storage
 *   - Allows deletion of stored credentials
 *   - Displays configuration status and security information
 *
 * RISKS:
 *   - WARN: Private key and password are visible when show toggle is enabled
 *   - NOTE: Credentials are cleared from state after save for security
 *   - WARN: Key deletion may not invalidate active build processes
 *   - NOTE: No validation of key format before saving
 *
 * @returns {JSX.Element} GitHub settings view component
 *
 */

/**
 * Message type for status messages
 */
type MessageType = "error" | "info" | "success";

/**
 * WHAT:
 *   Helper function to get CSS classes for message card based on message type.
 *
 * @param messageType - Type of message (success, error, or other)
 *
 * @returns {string} CSS classes for the message card
 *
 */
function getMessageCardClassName(messageType: MessageType): string {
  if (messageType === "success") {
    return "bg-green-500/10 border-green-500/20";
  }
  if (messageType === "error") {
    return "bg-red-500/10 border-red-500/20";
  }
  return "bg-blue-500/10 border-blue-500/20";
}

/**
 * WHAT:
 *   Helper function to get message icon component based on message type.
 *
 * @param messageType - Type of message (success, error, or other)
 *
 * @returns {React.JSX.Element} Icon component for the message
 *
 */
function getMessageIcon(messageType: MessageType): React.JSX.Element {
  if (messageType === "success") {
    return <CheckCircle className="w-5 h-5 text-green-400" />;
  }
  if (messageType === "error") {
    return <XCircle className="w-5 h-5 text-red-400" />;
  }
  return <AlertCircle className="w-5 h-5 text-blue-400" />;
}

/**
 * WHAT:
 *   Helper function to get CSS classes for message text based on message type.
 *
 * @param messageType - Type of message (success, error, or other)
 *
 * @returns {string} CSS classes for the message text
 *
 */
function getMessageTextClassName(messageType: MessageType): string {
  if (messageType === "success") {
    return "text-green-300";
  }
  if (messageType === "error") {
    return "text-red-300";
  }
  return "text-blue-300";
}

/**
 * WHAT:
 *   React component for managing GitHub Tauri signing keys for automated builds.
 *
 * WHY:
 *   Enables users to configure signing credentials for local Tauri builds, providing
 *   faster build times compared to GitHub Actions while maintaining security.
 *
 * @returns {JSX.Element} GitHub settings view component
 *
 */
export function GitHubSettingsView() {
  const [privateKey, setPrivateKey] = useState("");
  const [password, setPassword] = useState("");
  const [showPrivateKey, setShowPrivateKey] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isConfigured, setIsConfigured] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState<null | {
    type: "error" | "info" | "success";
    text: string;
  }>(null);

  useEffect(() => {
    void checkSigningKeysConfigured();
  }, []);

  /**
   * WHAT:
   *   Checks if signing keys are configured in secure storage.
   *
   * WHY:
   *   Determines configuration status to show appropriate UI (form vs. management view).
   *
   * SIDE EFFECTS:
   *   - Invokes backend command to check key configuration
   *   - Updates isConfigured state
   *
   * RISKS:
   *   - WARN: Fails silently if backend command is unavailable
   *   - NOTE: Only checks existence, not validity of keys
   */
  const checkSigningKeysConfigured = async () => {
    try {
      const configured = await core.invoke("check_signing_keys_configured");
      setIsConfigured(configured as boolean);
    } catch {
      // NOTE: Failed to check signing keys - fails silently (non-critical)
    }
  };

  /**
   * WHAT:
   *   Saves signing keys to secure storage.
   *
   * WHY:
   *   Persists credentials for automated Tauri builds.
   *
   * SIDE EFFECTS:
   *   - Validates required fields (private key and password)
   *   - Saves private key to secure storage
   *   - Saves password to secure storage
   *   - Clears sensitive data from component state
   *   - Updates configuration status
   *   - Displays success/error messages
   *
   * RISKS:
   *   - WARN: No validation of key format before saving
   *   - NOTE: Credentials are cleared from state after save for security
   *   - WARN: Error messages may not provide specific failure reasons
   */
  const handleSaveKeys = async () => {
    if (!privateKey.trim() || !password.trim()) {
      setMessage({
        text: "Private Key and Password are required",
        type: "error",
      });
      return;
    }

    setIsLoading(true);
    setMessage(null);

    try {
      await core.invoke("set_tauri_signing_private_key", { privateKey });
      await core.invoke("set_tauri_signing_password", { password });

      setMessage({
        text: "GitHub Signing Keys saved successfully!",
        type: "success",
      });
      setIsConfigured(true);

      // NOTE: Clear sensitive data from state after successful save
      setPrivateKey("");
      setPassword("");
    } catch (error) {
      const message = error instanceof Error ? error.message : "Error saving signing keys";
      setMessage({ text: message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * WHAT:
   *   Deletes signing keys from secure storage.
   *
   * WHY:
   *   Allows users to remove stored credentials and disable automated signing.
   *
   * SIDE EFFECTS:
   *   - Clears private key from secure storage (sets empty string)
   *   - Clears password from secure storage (sets empty string)
   *   - Updates configuration status to unconfigured
   *   - Displays success/error messages
   *
   * RISKS:
   *   - WARN: Key deletion may not invalidate active build processes
   *   - NOTE: No confirmation dialog - keys are deleted immediately
   *   - WARN: Deletion cannot be undone
   */
  const handleClearKeys = async () => {
    setIsLoading(true);
    setMessage(null);

    try {
      // NOTE: Clear from secure storage by setting empty values
      await core.invoke("set_tauri_signing_private_key", { privateKey: "" });
      await core.invoke("set_tauri_signing_password", { password: "" });

      setMessage({
        text: "Signing Keys deleted successfully!",
        type: "success",
      });
      setIsConfigured(false);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Error deleting signing keys";
      setMessage({ text: message, type: "error" });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header Section */}
      <Card className="bg-slate-800/30 border-slate-700/50" padding="lg" variant="default">
        <Stack align="center" direction="row" gap={4}>
          <div className="p-3 bg-tracq-bright-blue/10 rounded-lg">
            <Code className="w-6 h-6 text-tracq-bright-blue" />
          </div>
          <Stack direction="col" gap={1}>
            <Heading className="text-xl font-semibold text-white" level={2}>
              GitHub Integration
            </Heading>
            <p className="text-slate-300">
              Manage Tauri signing keys for automated builds and secure deployments.
            </p>
          </Stack>
        </Stack>
      </Card>

      {/* Status Indicators */}
      <div className="space-y-4">
        <Heading className="text-lg font-medium text-white flex items-center gap-2" level={3}>
          <Shield className="w-5 h-5 text-slate-400" />
          Signing Keys Status
        </Heading>

        <div className="grid gap-4 md:grid-cols-2">
          <StatusIndicator
            description="Tauri signing private key for automated builds"
            isConfigured={isConfigured}
            label="Private Key"
            showLock
            size="md"
          />
          <StatusIndicator
            description="Password for the private key"
            isConfigured={isConfigured}
            label="Signing Password"
            showLock
            size="md"
          />
        </div>
      </div>

      {/* Configuration Form */}
      {!isConfigured && (
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Stack direction="col" gap={6}>
            {/* Form Header */}
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-orange-500/10 rounded-lg">
                <Key className="w-5 h-5 text-orange-500" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                Configure Signing Keys
              </Heading>
            </Stack>

            {/* Private Key Field */}
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span>Private Key</span>
                <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Textarea
                  className="w-full bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-orange-500 focus:ring-orange-500/20 resize-none pr-12"
                  id="private-key"
                  onChange={setPrivateKey}
                  placeholder="-----BEGIN PRIVATE KEY-----"
                  rows={6}
                  value={privateKey}
                />
                <button
                  className="absolute right-2 top-2 p-1.5 rounded-md text-slate-400 hover:text-slate-300 hover:bg-slate-600/50 transition-colors"
                  onClick={() => {
                    setShowPrivateKey(!showPrivateKey);
                  }}
                  title={showPrivateKey ? "Hide private key" : "Show private key"}
                  type="button"
                >
                  {showPrivateKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-slate-500 text-xs">
                Copy the Private Key from your GitHub Actions Secrets (TAURI_SIGNING_PRIVATE_KEY)
              </p>
            </Stack>

            {/* Password Field */}
            <Stack direction="col" gap={2}>
              <Label className="text-slate-300 flex items-center gap-2">
                <Lock className="w-4 h-4" />
                <span>Signing Password</span>
                <span className="text-red-400">*</span>
              </Label>
              <div className="relative">
                <Input
                  className="w-full bg-slate-700 border-slate-600 text-white placeholder-slate-400 focus:border-orange-500 focus:ring-orange-500/20 pr-12"
                  id="password"
                  onChange={(e) => {
                    setPassword(e.target.value);
                  }}
                  placeholder="Enter your signing password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                />
                <button
                  className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1.5 rounded-md text-slate-400 hover:text-slate-300 hover:bg-slate-600/50 transition-colors"
                  onClick={() => {
                    setShowPassword(!showPassword);
                  }}
                  title={showPassword ? "Hide password" : "Show password"}
                  type="button"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-slate-500 text-xs">
                The password from your GitHub Actions Secrets (TAURI_SIGNING_PRIVATE_KEY_PASSWORD)
              </p>
            </Stack>

            {/* Security Warning */}
            <div className="flex items-start gap-2 p-3 bg-yellow-500/10 border border-yellow-500/20 rounded-md">
              <AlertCircle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-yellow-300 text-xs font-medium">Security Notice</p>
                <p className="text-yellow-200 text-xs">
                  This information will be stored securely. Only use the show/hide toggle when
                  necessary.
                </p>
              </div>
            </div>

            {/* Save Button */}
            <Button
              className="w-full sm:w-auto"
              disabled={isLoading || !privateKey.trim() || !password.trim()}
              onClick={() => {
                void handleSaveKeys();
              }}
              size="md"
              variant="primary"
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Save Signing Keys
                </>
              )}
            </Button>
          </Stack>
        </Card>
      )}

      {/* Clear Keys Section */}
      {isConfigured ? (
        <Card className="bg-slate-800/50 border-slate-700/50" padding="lg" variant="default">
          <Stack direction="col" gap={4}>
            {/* Section Header */}
            <Stack align="center" direction="row" gap={3}>
              <div className="p-2 bg-green-500/10 rounded-lg">
                <Shield className="w-5 h-5 text-green-500" />
              </div>
              <Heading className="text-lg font-medium text-white" level={3}>
                Manage Signing Keys
              </Heading>
            </Stack>

            {/* Management Content */}
            <Stack align="center" className="flex-wrap gap-4" direction="row" justify="between">
              <Stack direction="col" gap={1}>
                <p className="text-slate-300 text-sm">
                  Your signing keys are securely stored and will be used automatically during the
                  build process.
                </p>
                <p className="text-slate-500 text-xs">
                  Local builds are significantly faster than GitHub Actions!
                </p>
              </Stack>

              <Button
                disabled={isLoading}
                onClick={() => {
                  void handleClearKeys();
                }}
                size="md"
                variant="danger"
              >
                {isLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  <>
                    <Trash2 className="w-4 h-4 mr-2" />
                    Delete Keys
                  </>
                )}
              </Button>
            </Stack>
          </Stack>
        </Card>
      ) : null}

      {/* Message Display */}
      {message ? (
        <Card
          className={`border ${getMessageCardClassName(message.type)}`}
          padding="md"
          variant="default"
        >
          <Stack align="center" direction="row" gap={3}>
            {getMessageIcon(message.type)}
            <span className={`text-sm font-medium ${getMessageTextClassName(message.type)}`}>
              {message.text}
            </span>
          </Stack>
        </Card>
      ) : null}

      {/* Information Cards */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Security Info */}
        <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
          <Stack align="start" direction="row" gap={3}>
            <Lock className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <Stack direction="col" gap={2}>
              <Heading className="text-sm font-medium text-slate-300" level={4}>
                Secure Storage & Automation
              </Heading>
              <ul className="space-y-1 text-slate-400 text-sm">
                <li>• Keys stored in Tauri Secure Storage</li>
                <li>• Automatic usage during build process</li>
                <li>• Local builds are much faster than GitHub Actions</li>
                <li>• No manual configuration needed for each build</li>
              </ul>
            </Stack>
          </Stack>
        </Card>

        {/* Performance Info */}
        <Card className="bg-slate-800/20 border-slate-700/30" padding="md" variant="default">
          <Stack align="start" direction="row" gap={3}>
            <Zap className="w-5 h-5 text-slate-400 mt-0.5 flex-shrink-0" />
            <Stack direction="col" gap={2}>
              <Heading className="text-sm font-medium text-slate-300" level={4}>
                Performance Benefits
              </Heading>
              <ul className="space-y-1 text-slate-400 text-sm">
                <li>• 5-10x faster than GitHub Actions</li>
                <li>• No queue waiting times</li>
                <li>• Full control over build environment</li>
                <li>• Offline development capability</li>
              </ul>
            </Stack>
          </Stack>
        </Card>
      </div>

      {/* Setup Guide */}
      <Card className="bg-blue-500/10 border-blue-500/20" padding="md" variant="default">
        <Stack align="start" direction="row" gap={3}>
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <Stack direction="col" gap={2}>
            <Heading className="text-sm font-medium text-blue-400" level={4}>
              How to Get Your Signing Keys
            </Heading>
            <div className="text-sm text-blue-300 space-y-1">
              <p>1. Go to your GitHub repository → Settings → Secrets and variables → Actions</p>
              <p>
                2. Find{" "}
                <code className="bg-blue-500/20 px-1 rounded">TAURI_SIGNING_PRIVATE_KEY</code> and
                copy the value
              </p>
              <p>
                3. Find{" "}
                <code className="bg-blue-500/20 px-1 rounded">
                  TAURI_SIGNING_PRIVATE_KEY_PASSWORD
                </code>{" "}
                and copy the value
              </p>
              <p>4. Paste both values above and save</p>
            </div>
          </Stack>
        </Stack>
      </Card>
    </div>
  );
}
